// Phase 5 of the telemetry plan (§8): the login summary, and the one place in
// the product that may send a report to somebody with no usage privileges.
//
// That is a deliberate exception to D14 — normally a report is rendered per
// recipient against the recipient's own live privileges, so it cannot show them
// data they are not entitled to. Here it is rendered once with the sender's
// view and the same PDF goes to every address.
//
// What keeps that defensible is narrowness, and the UI says so out loud rather
// than hiding it: the fields are fixed (user, sessions, total, average, last
// login — no projects, no cost, no per-action detail), the PDF footer names the
// sender, and every send is written to an audit trail shown right below.
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { fmtDateTime, fmtDuration, getJson, Tile } from './usageUi';
import { authHeaders } from '../../utils/authHeader';

const MAX_RECIPIENTS = 20;

/** Sort rows by a column, nulls last either way — an open session has no
 *  average, which is missing information rather than zero. */
function sortBy(rows, key, dir) {
  const sign = dir === 'asc' ? 1 : -1;
  return [...(rows || [])].sort((a, b) => {
    const x = a[key];
    const y = b[key];
    if (x === y) return 0;
    if (x === null || x === undefined) return 1;
    if (y === null || y === undefined) return -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * sign;
    return String(x).localeCompare(String(y)) * sign;
  });
}

const LoginSummary = ({ queryString }) => {
  const [data, setData] = useState(null);
  const [sends, setSends] = useState([]);
  const [sort, setSort] = useState({ key: 'total_s', dir: 'desc' });
  const [to, setTo] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([
        getJson(`/api/telemetry/login-summary?${queryString}`),
        getJson('/api/telemetry/login-summary/sends'),
      ]);
      setData(d.data || null);
      setSends(s.sends || []);
    } catch (e) {
      setError(e.message || 'Could not load the login summary');
    }
  }, [queryString]);

  useEffect(() => { load(); }, [load]);
  // A new filter set means a different report; an unsent confirmation for the
  // old one must not carry over.
  useEffect(() => { setConfirming(false); setResult(null); }, [queryString]);

  const recipients = useMemo(
    () => [...new Set(to.split(/[,;\s]+/).map((r) => r.trim().toLowerCase()).filter(Boolean))],
    [to]);
  const rows = useMemo(() => sortBy(data?.rows, sort.key, sort.dir), [data, sort]);

  const send = async () => {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(`/api/telemetry/login-summary/send?${queryString}`, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ recipients }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success) throw new Error(body.error || `send failed (${res.status})`);
      setResult(body);
      setTo('');
      setConfirming(false);
      load();
    } catch (e) {
      setError(e.message || 'Could not send the summary');
    } finally {
      setBusy(false);
    }
  };

  const th = (label, key, numeric) => (
    <th className={`ua-sort-th${numeric ? ' ua-num' : ''}${sort.key === key ? ' ua-sorted' : ''}`}>
      <button type="button"
        onClick={() => setSort({
          key, dir: sort.key === key && sort.dir === 'desc' ? 'asc' : 'desc',
        })}>
        {label}
        <span className="ua-sort-mark">
          {sort.key === key ? (sort.dir === 'desc' ? '▾' : '▴') : '⇅'}
        </span>
      </button>
    </th>
  );

  return (
    <>
      {error && <div className="usage-error" role="alert">{error}</div>}

      <p className="usage-hint">
        Time in the platform, per user, over the range in the filter bar. These
        are <strong>platform logins</strong>, not Safety Dashboard use — someone
        who signed in and never opened the dashboard still appears here.
        Averages cover closed sessions only; sessions still open are counted but
        have no length.
      </p>

      <div className="usage-tiles">
        <Tile label="Users" value={data?.users ?? '—'} />
        <Tile label="Sessions" value={data?.sessions ?? '—'} />
        <Tile label="Total time" value={fmtDuration(data?.total_s)} />
      </div>

      {rows.length ? (
        <div className="usage-table-wrap">
          <table className="usage-table">
            <thead>
              <tr>
                {th('User', 'user_email')}
                {th('Customer', 'customer')}
                {th('Sessions', 'sessions', true)}
                {th('Total time', 'total_s', true)}
                {th('Average', 'avg_s', true)}
                {th('Last login', 'last_login')}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.user_email}>
                  <td>{r.user_email}</td>
                  <td>{r.customer || '—'}</td>
                  <td className="ua-num">
                    {r.sessions}
                    {r.open_sessions ? <em> ({r.open_sessions} open)</em> : null}
                  </td>
                  <td className="ua-num">{fmtDuration(r.total_s)}</td>
                  <td className="ua-num">
                    {r.avg_s === null || r.avg_s === undefined ? '—' : fmtDuration(r.avg_s)}
                  </td>
                  <td>{fmtDateTime(r.last_login)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="usage-empty">No logins in this range.</p>}

      <h3>Send this summary</h3>
      <div className="ua-send">
        <p className="usage-hint">
          The PDF is built once from <strong>your</strong> view and the same
          document goes to every address — including people with no usage
          access, which is the point of this report. It carries only the columns
          above: no project names, no cost, no per-action detail. Its footer
          names you as the authoriser and restates the filters, and the send is
          recorded below.
        </p>
        <div className="ua-send-row">
          <input type="text" className="ua-send-to"
            placeholder="addresses, comma or space separated"
            value={to} disabled={busy}
            onChange={(e) => { setTo(e.target.value); setConfirming(false); }}
            aria-label="Recipients" />
          {!confirming ? (
            <button type="button" className="ua-send-btn"
              disabled={!recipients.length || busy || !data?.users}
              onClick={() => setConfirming(true)}>
              Send…
            </button>
          ) : (
            <>
              <button type="button" className="ua-send-btn ua-send-btn-danger"
                disabled={busy} onClick={send}>
                {busy ? 'Sending…' : `Yes, e-mail ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`}
              </button>
              <button type="button" className="ua-send-btn" disabled={busy}
                onClick={() => setConfirming(false)}>Cancel</button>
            </>
          )}
        </div>
        {recipients.length > MAX_RECIPIENTS && (
          <p className="usage-error">
            {recipients.length} addresses — at most {MAX_RECIPIENTS} per send.
          </p>
        )}
        {confirming && (
          <p className="ua-send-confirm">
            About to e-mail {data?.users} user{data?.users === 1 ? '' : 's'}&apos;
            login totals to: {recipients.join(', ')}
          </p>
        )}
        {result && (
          <p className="ua-send-ok">
            Sent to {result.sent?.join(', ')} ({result.users_in_report} users in the report).
            {result.errors?.length ? ` Failed: ${result.errors.join('; ')}` : ''}
          </p>
        )}
      </div>

      <h3>Sends so far</h3>
      {sends.length ? (
        <div className="usage-table-wrap">
          <table className="usage-table">
            <thead>
              <tr>
                <th>When</th><th>Authorised by</th><th>To</th>
                <th className="ua-num">Users</th><th>Range</th><th>How</th>
              </tr>
            </thead>
            <tbody>
              {sends.map((s, i) => (
                <tr key={i}>
                  <td>{fmtDateTime(s.ts)}</td>
                  <td>{s.authorised_by}</td>
                  <td>{(s.recipients || []).join(', ')}</td>
                  <td className="ua-num">{s.users_in_report}</td>
                  <td>
                    {s.range_from ? fmtDateTime(s.range_from) : '—'}
                    {' .. '}
                    {s.range_to ? fmtDateTime(s.range_to) : 'now'}
                  </td>
                  <td>{s.trigger}{s.schedule_id ? ` (${s.schedule_id.slice(0, 8)})` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="usage-empty">
          Nothing has been sent yet. Every send is recorded here — that trail is
          part of what makes sending to people without usage access acceptable.
        </p>
      )}
    </>
  );
};

export default LoginSummary;
